import { ShieldCheck } from "lucide-react";
import { useState } from "react";

import { CardActions, createCardActionsProps } from "@/src/components/cards/card-actions";
import { EntityDropdownMenu, Field, FieldLayout, hasConflicts, renderBadge } from "@/src/components/cards/common";
import CoverageCardEditable from "@/src/components/cards/coverage-card/coverage-card-editable";
import { Card, CardTitle, CardHeader, CardContent } from "@/src/components/shadcn/ui/card";
import { ContextMenu, ContextMenuTrigger } from "@/src/components/shadcn/ui/context-menu";
import { useUser } from "@/src/context/user-context";
import { canWriteSOR } from "@/src/lib/rbac";
import { Coverage, CoverageType } from "@/src/models/coverage";

const COVERAGE_FIELDS: Field[] = [
  { key: "payer", label: "Payer", comparable: true },
  { key: "plan", label: "Plan Name", comparable: true },
  { key: "effectiveDate", label: "Effective Date", type: "date", comparable: true },
  { key: "subscriberId", label: "Subscriber ID", comparable: true },
  { key: "groupNumber", label: "Group Number", comparable: true },
  { key: "subscriberGivenName", label: "Subscriber Given Name", comparable: true },
  { key: "subscriberFamilyName", label: "Subscriber Family Name", comparable: true },
  { key: "lineOfBusiness", label: "Line of Business", comparable: true },
  { key: "relationshipToSubscriber", label: "Relationship to Subscriber", comparable: true },
  { key: "expirationDate", label: "Expiration Date", type: "date", comparable: false },
  { key: "phoneNumber", label: "Phone Number", type: "tel", comparable: false },
  { key: "faxPhoneNumber", label: "Fax Phone Number", type: "tel", comparable: false },
];

export default function CoverageCard({
  coverage,
  type,
  isEditable = false,
  compareMode = false,
  secondaryCoverage = null,
  eligibilityStatus = null,
}: {
  coverage: Coverage;
  type: "Primary" | "Secondary";
  isEditable?: boolean;
  compareMode?: boolean;
  secondaryCoverage?: Coverage | null;
  eligibilityStatus?: "active" | "inactive" | null;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const { roles } = useUser();
  const canWrite = canWriteSOR(roles);
  const effectiveIsEditable = isEditable && canWrite;

  if (!coverage) {
    return null;
  }

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  if (isEditing) {
    return <CoverageCardEditable coverage={coverage} onClose={handleEditToggle} type={type} />;
  }

  let hasAnyConflicts = false;

  if (compareMode && secondaryCoverage) {
    hasAnyConflicts = hasConflicts(COVERAGE_FIELDS, coverage, secondaryCoverage);
  }

  const cardActionsProps = createCardActionsProps({
    id: coverage.id,
    label: "Coverage ID",
    customProps: [{ label: "Edit Coverage", onClick: handleEditToggle }],
  });

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <Card className="w-full" tabIndex={0}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <ShieldCheck className="h-6 w-6 mr-2" />
                <CardTitle className="text-md font-semibold">{`${type} Coverage`}</CardTitle>
              </div>
              <div className="ml-auto flex items-center gap-2">
                {eligibilityStatus === "active" && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-success/20 text-success">
                    Eligible
                  </span>
                )}
                {eligibilityStatus === "inactive" && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-destructive/20 text-destructive">
                    Ineligible
                  </span>
                )}
                {renderBadge(compareMode, secondaryCoverage, hasAnyConflicts)}
                {effectiveIsEditable && <EntityDropdownMenu cardActionsProps={cardActionsProps} />}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {coverage.coverageType === CoverageType.SELF_PAY ? (
              <div className="text-sm">
                <span className="font-semibold">Coverage Type:</span> Self Pay
              </div>
            ) : (
              <FieldLayout
                fields={COVERAGE_FIELDS}
                patient={coverage}
                secondaryObject={secondaryCoverage}
                compareMode={compareMode}
              />
            )}
          </CardContent>
        </Card>
      </ContextMenuTrigger>
      {effectiveIsEditable && <CardActions {...cardActionsProps} isContextMenu={true} />}
    </ContextMenu>
  );
}
